import { FeatureCollection, GeoJsonProperties, Geometry, Feature } from "geojson";
import { GeoFileType, GroupedGeoJSONFiles, PropertyWithExamples } from "./BulkImportForm.types";

/**
 * Counts the number of files of a specific GeoJSON type
 */
export function filesCount(selectableGeoData: GroupedGeoJSONFiles, type: GeoFileType): number {
	const count = Object.values(selectableGeoData).reduce((count, types) => {
		return count + (types[type] ? types[type].length : 0);
	}, 0);
	return count;
}

/**
 * Extracts all unique property names from GeoJSON feature collections
 * Returns up to 3 examples per property
 */
export function extractAllPropertiesFromGeoData(
	geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
): PropertyWithExamples[] {
	const propertyMap = new Map<string, Set<string>>();

	// Collect unique values for each property
	for (const geoJson of geoJsons) {
		for (const feature of geoJson.features || []) {
			if (feature.properties) {
				Object.entries(feature.properties).forEach(([key, value]) => {
					if (value !== null && value !== undefined && String(value).trim() !== "") {
						if (!propertyMap.has(key)) {
							propertyMap.set(key, new Set<string>());
						}
						// Add the string representation of the value
						propertyMap.get(key)!.add(String(value));
					}
				});
			}
		}
	}

	// Convert to array format with up to 3 examples per property
	const result: PropertyWithExamples[] = [];
	for (const [propertyName, values] of Array.from(propertyMap.entries())) {
		const examples = Array.from(values).slice(0, 3) as string[];
		result.push({ name: propertyName, examples });
	}

	// Sort by property name
	return result.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Extracts a specific property value from GeoJSON features based on property name(s).
 * Supports both single property name and array of property names.
 * When given an array, tries each property name in order - first match wins.
 */
export function extractPropertyValueFromGeoData(
	geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
	propertyName: string | string[] | null,
): string {
	if (!propertyName) return "";

	// Normalize to array for consistent handling
	const propertyNames = Array.isArray(propertyName) ? propertyName : [propertyName];
	if (propertyNames.length === 0) return "";

	// Try each property name in order (first match wins)
	for (const propName of propertyNames) {
		for (const geoJson of geoJsons) {
			for (const feature of geoJson.features || []) {
				if (feature.properties && feature.properties[propName]) {
					const value = feature.properties[propName];
					if (value !== null && value !== undefined && String(value).trim() !== "") {
						let str = String(value);
						if (str.toLowerCase().endsWith(".zip")) {
							str = str.slice(0, -4);
						}
						return str;
					}
				}
			}
		}
	}

	return "";
}

/**
 * Extracts unique file paths from features
 */
export function extractFilePaths(features: Feature[]): string[] {
	const paths = new Set<string>();
	features.forEach((feature) => {
		if (feature?.properties?.FilePath) {
			paths.add(feature.properties.FilePath);
		}
	});
	return Array.from(paths);
}

/**
 * Groups features by their FilePath property
 */
export function groupByFilePath(features: Feature[]): Map<string, Feature[]> {
	const groups = new Map<string, Feature[]>();
	features.forEach((feature) => {
		const filePath = feature?.properties?.FilePath || "Unknown";
		if (!groups.has(filePath)) {
			groups.set(filePath, []);
		}
		groups.get(filePath)!.push(feature);
	});
	return groups;
}

/**
 * Interface representing a matched GeoData result with features from different types
 */
export interface GeoDataMatchResult {
	boundary?: FeatureCollection<Geometry, GeoJsonProperties>;
	zones?: FeatureCollection<Geometry, GeoJsonProperties>;
	centroids?: FeatureCollection<Geometry, GeoJsonProperties>;
	cores?: FeatureCollection<Geometry, GeoJsonProperties>;
}

/**
 * Extracts a property value using priority-based fallback across filter types.
 * Priority: Boundary → Zones → Centroids → Cores
 *
 * @param geoDataMatch - The matched GeoData object containing features from all types
 * @param propertyNames - Array of property names to search for (in order of preference)
 * @returns The first found property value, or null if not found in any type
 */
export function extractPropertyValueWithPriority(
	geoDataMatch: GeoDataMatchResult,
	propertyNames: string[] | null,
): string | null {
	if (!propertyNames || propertyNames.length === 0) return null;

	// Priority order: Boundary → Zones → Centroids → Cores
	const priorityOrder: Array<keyof GeoDataMatchResult> = ["boundary", "zones", "centroids", "cores"];

	for (const type of priorityOrder) {
		const featureCollection = geoDataMatch[type];
		if (featureCollection && featureCollection.features && featureCollection.features.length > 0) {
			// Try each property name in order
			for (const propName of propertyNames) {
				for (const feature of featureCollection.features) {
					const value = feature.properties?.[propName];
					if (value !== null && value !== undefined && String(value).trim() !== "") {
						return String(value);
					}
				}
			}
		}
	}

	return null;
}

/**
 * Checks if a date is within a reasonable range for agricultural field dates.
 * Returns true if date is between year 2000 and 10 years from now.
 */
function isReasonableDate(date: Date): boolean {
	const year = date.getFullYear();
	const currentYear = new Date().getFullYear();
	// Reject dates before 2000 (catches epoch-era dates) or more than 10 years in future
	return year >= 2000 && year <= currentYear + 10;
}

/**
 * Parses a GeoJSON property value into a Date object.
 * Handles: Date objects, ISO strings, Unix timestamps (ms), US formats.
 * Returns undefined for invalid/unparseable values or dates outside reasonable range.
 *
 * This is the first date field in PropertyMappings - extracted values must be
 * converted from string to Date when applied to Job.dateFieldReadyEstimated.
 */
export function parseGeoJSONDateValue(value: unknown): Date | undefined {
	if (value === null || value === undefined) return undefined;

	// Already a Date object (some parsers return Date objects)
	if (value instanceof Date) {
		if (isNaN(value.getTime())) return undefined;
		return isReasonableDate(value) ? value : undefined;
	}

	// Unix timestamp (number in milliseconds) - must be positive
	if (typeof value === "number") {
		if (value <= 0) return undefined;
		const date = new Date(value);
		if (isNaN(date.getTime())) return undefined;
		return isReasonableDate(date) ? date : undefined;
	}

	// String parsing
	if (typeof value === "string") {
		const str = value.trim();
		if (!str) return undefined;

		// Check if it's a numeric string (Unix timestamp)
		const num = Number(str);
		if (!isNaN(num) && num > 0) {
			const date = new Date(num);
			if (!isNaN(date.getTime()) && isReasonableDate(date)) return date;
		}

		// Try ISO format first (YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss.sssZ)
		let date = new Date(str);
		if (!isNaN(date.getTime()) && isReasonableDate(date)) return date;

		// Try US format (MM/DD/YYYY or M/D/YYYY)
		const usMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
		if (usMatch) {
			const [, month, day, year] = usMatch;
			date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
			if (!isNaN(date.getTime()) && isReasonableDate(date)) return date;
		}
	}

	return undefined;
}

/**
 * Parses a string value to a positive number.
 * Returns null for invalid input, empty strings, zero, or negative numbers.
 */
export function parsePositiveNumber(value: string | null | undefined): number | null {
	if (!value) return null;
	const trimmed = String(value).trim();
	if (trimmed === "") return null;
	const num = parseFloat(trimmed);
	// Must be a valid, finite, positive number
	if (isNaN(num) || !isFinite(num) || num <= 0) {
		return null;
	}
	return num;
}

/**
 * Cleans __groupKey property from GeoJSON features
 */
export function removeTagFromGeoJSON(
	geojson: FeatureCollection<Geometry, GeoJsonProperties> | undefined,
): FeatureCollection<Geometry, GeoJsonProperties> | undefined {
	if (!geojson) return undefined;
	const cleaned: FeatureCollection<Geometry, GeoJsonProperties> = JSON.parse(JSON.stringify(geojson));
	cleaned.features.forEach((feature) => {
		if (feature.properties && feature.properties.__groupKey) {
			delete feature.properties.__groupKey;
		}
	});
	return cleaned;
}
